import { useEffect, useMemo, useRef, useState } from 'react';
import type { IReportProject } from 'app/services/reportsBoardApi';

/**
 * Project dropdown — port of the shared Backbone widget
 * static/mvc/view/searchable_select.js + tmpl/searchable_select(.options).tmpl
 * as configured by report/mvc/view/main_header.js:82-88 (select_name 'Project',
 * all: true, search + scroll wired to the projects pagination).
 *
 * Markup kept identical (rand-N marker classes, .selectdropbtn open/close
 * classes, #searchableDropdown .dropdown-content, ul.searchableDropdown-list)
 * so the legacy common.css/report styles apply unchanged.
 *
 * Ported behaviors:
 *  - open/close via the button; a document click on anything that doesn't
 *    carry the rand-N class closes the panel (searchable_select.js:27-39 —
 *    including the quirk that clicking the reset "X" merely closes it).
 *  - 'top' class when there is more room above (show(), :107-134).
 *  - 500ms debounced search, lowercased+trimmed before hitting the server
 *    (debounce :74-85, filter :86-106); duplicate values skipped.
 *  - ArrowUp/ArrowDown/Enter keyboard hover + select (:40-72), hover scroll
 *    at 23px per row.
 *  - infinite scroll: onScrollEnd when within 250px of the bottom
 *    (checkScroll :195-203).
 *  - 'All' row shown only while not searching (options tmpl + :94-98).
 */
interface ProjectSearchableSelectProps {
  /** pinned first, then the paginated main list (main_header.js:265-273). */
  list: IReportProject[];
  selectedId: string | null;
  /** Button caption — page-computed (selected name or 'All'). */
  name: string;
  onSelect: (id: number | null, name: string) => void;
  onSearch: (value: string) => void;
  onScrollEnd: () => void;
}

const ProjectSearchableSelect = ({
  list,
  selectedId,
  name,
  onSelect,
  onSearch,
  onScrollEnd,
}: ProjectSearchableSelectProps) => {
  // Stable per-mount marker, like the legacy `rand-N` (searchable_select.js:23).
  const random = useMemo(() => Math.floor(Math.random() * 100000), []);
  const [opened, setOpened] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [hovered, setHovered] = useState(-1);
  const [dropUp, setDropUp] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<number | null>(null);
  const lastSentRef = useRef('');

  const rand = `rand-${random}`;

  const close = () => {
    setOpened(false);
    setHovered(-1);
    if (listRef.current) listRef.current.scrollTop = 0;
  };

  // Document click: anything without the rand-N marker closes the dropdown
  // (searchable_select.js:27-39). List rows select via their own handler first.
  useEffect(() => {
    if (!opened) return undefined;
    const onDocClick = (e: MouseEvent) => {
      const cls = (e.target as HTMLElement)?.className;
      if (typeof cls === 'string' && cls.includes(rand)) return;
      close();
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [opened, rand]);

  // Keyboard hover/select while opened (searchable_select.js:40-72).
  useEffect(() => {
    if (!opened) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.keyCode !== 40 && e.keyCode !== 38 && e.keyCode !== 13) return;
      if (e.keyCode === 13) {
        const index = hovered > -1 ? hovered : 0;
        const item = list[index];
        if (item) selectItem(item.id, item.name);
        return;
      }
      setHovered((prev) => {
        let next = prev;
        if (e.keyCode === 40) {
          next = prev === list.length - 1 ? 0 : prev + 1;
        } else if (prev) {
          next = prev - 1;
        }
        if (listRef.current) listRef.current.scrollTop = next * 23;
        return next;
      });
    };
    document.body.addEventListener('keydown', onKeyDown);
    return () => document.body.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, hovered, list]);

  const open = () => {
    const select = rootRef.current;
    if (select) {
      const rect = select.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropUp(spaceAbove - 350 > spaceBelow);
    }
    setOpened(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (opened) close();
    else open();
  };

  const selectItem = (id: number | null, itemName: string) => {
    close();
    setSearchValue('');
    lastSentRef.current = '';
    onSelect(id, itemName);
  };

  // The widget renders inside <label class="projects-wrapper">
  // (main_header.tmpl:2) and the label's labelled control is the
  // .selectdropbtn, so a click on a non-interactive row makes the browser
  // forward a second click to that button — which toggles the panel straight
  // back open over the freshly picked caption. Legacy got away with it because
  // select_item re-rendered the header and Searchable_select.render()
  // (searchable_select.js:236-243) resets `opened` and replaces the markup;
  // React keeps the component mounted, so cancel the label activation instead.
  const selectRow = (e: React.MouseEvent, id: number | null, itemName: string) => {
    e.preventDefault();
    selectItem(id, itemName);
  };

  // 500ms debounce, trimmed + lowercased (debounce :74-85).
  const queueSearch = (value: string) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      onSearch(value.trim().toLowerCase());
      setHovered(-1);
      if (listRef.current) listRef.current.scrollTop = 0;
    }, 500);
  };

  const onFilterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 38 || e.keyCode === 40) return;
    const value = (e.target as HTMLInputElement).value.trim();
    if (lastSentRef.current === value && e.keyCode !== 13) return;
    lastSentRef.current = value;
    if (e.keyCode === 13) {
      const index = hovered > -1 ? hovered : 0;
      const item = list[index];
      if (item) selectItem(item.id, item.name);
    } else {
      queueSearch(value);
    }
  };

  const onListScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const el = e.currentTarget;
    const bottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (bottom < 250) onScrollEnd();
  };

  return (
    <>
      <span>Project</span>
      <div ref={rootRef} className={`searchable-select-dropdown ${rand}`}>
        <button
          type="button"
          className={`${rand} btn-click selectdropbtn ${opened ? 'opened' : 'closed'}`}
          onClick={toggle}
        >
          <span className={`${rand} btn-click btn-name`}>{name || '----'}</span>
          <span className={`${rand} btn-click icon-down`}>&#9660;</span>
        </button>
        <div id="searchableDropdown" className={`dropdown-content ${rand}${dropUp ? ' top' : ''}`}>
          <input
            ref={inputRef}
            type="search"
            placeholder="Search.."
            className={`${rand} searchableInput`}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyUp={onFilterKey}
          />
          <span className={`reset-input${searchValue && opened ? '' : ' hide'}`}>X</span>
          <ul className="searchableDropdown-list" ref={listRef} onScroll={onListScroll}>
            {!searchValue && (
              <li
                data-value=""
                className="all-li"
                onClick={(e) => selectRow(e, null, 'All')}
              >
                All
              </li>
            )}
            {list.map((p, index) => (
              <li
                key={`${p.id}-${index}`}
                data-index={index}
                data-value={p.id}
                className={`${String(p.id) === selectedId ? `selected ${p.name}` : ''}${
                  index === hovered ? ' hovered' : ''
                }`}
                onClick={(e) => selectRow(e, p.id, p.name)}
              >
                {p.name}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
};

export default ProjectSearchableSelect;
